import { useState, useEffect, useMemo } from "react";
import MuiDialog from "@mui/material/Dialog";
import MuiIconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { MONO } from "../theme/tokens";
import { useAppTheme } from "../theme/ThemeContext";
import { api } from "../utils/helpers";
import IflowDiagram from "./IflowDiagram";

// BPMN parsing and diff

function parseBpmnElements(xmlStr){
  try{
    const p=new DOMParser();const doc=p.parseFromString(xmlStr,"application/xml");
    const res={};
    const tags=["serviceTask","scriptTask","callActivity","CallActivity","task","receiveTask","sendTask",
      "exclusiveGateway","inclusiveGateway","parallelGateway","eventBasedGateway",
      "startEvent","endEvent","intermediateCatchEvent","intermediateThrowEvent","subProcess","participant","messageFlow"];
    for(const tag of tags){
      const els=doc.getElementsByTagNameNS("*",tag);
      for(const el of els){
        const id=el.getAttribute("id");
        if(!id)continue;
        const name=el.getAttribute("name")||"";
        const properties={};
        const propEls=el.getElementsByTagNameNS("*","property");
        for(const pe of propEls){
          const keyEl=pe.getElementsByTagNameNS("*","key")[0];
          const valEl=pe.getElementsByTagNameNS("*","value")[0];
          const k=keyEl?keyEl.textContent.trim():pe.getAttribute("key")||"";
          const v2=valEl?valEl.textContent.trim():pe.getAttribute("value")||"";
          if(k)properties[k]=v2;
        }
        const bpmnType=el.localName||tag;
        const sortedEntries=Object.entries(properties).sort(([a],[b])=>a.localeCompare(b));
        const fingerprint=name+"|"+sortedEntries.map(([k,v2])=>k+"="+v2).join("|");
        const activityType=properties.activityType||"";
        const subActivityType=properties.subActivityType||"";
        const cmdUri=properties.cmdVariantUri||"";
        const cnameMatch=cmdUri.match(/cname::([^/]+)/);
        const cname=cnameMatch?cnameMatch[1]:"";
        res[id]={name,fingerprint,type:bpmnType,properties,activityType,subActivityType,cname};
      }
    }
    return res;
  }catch{return{};}
}

function diffBpmnElements(before,after){
  const added=[],removed=[],changed=[],unchanged=[];
  const allIds=new Set([...Object.keys(before),...Object.keys(after)]);
  for(const id of allIds){
    if(!before[id])added.push(id);
    else if(!after[id])removed.push(id);
    else if(before[id].fingerprint!==after[id].fingerprint)changed.push(id);
    else unchanged.push(id);
  }
  return{added,removed,changed,unchanged};
}

// Script line diff (GitHub style, LCS based).
// before = target/previous content, after = source/new content.
// Returns: [{type:'ctx'|'add'|'del', text, aLine, bLine}]
function computeLineDiff(before, after){
  const A=(before==null?"":String(before)).split("\n");
  const B=(after==null?"":String(after)).split("\n");
  const n=A.length, m=B.length;
  const MAX=2500; // guard against an O(n*m) blowup on huge scripts
  if(n>MAX||m>MAX){
    return [...A.map((t,i)=>({type:"del",text:t,aLine:i+1})),...B.map((t,i)=>({type:"add",text:t,bLine:i+1}))];
  }
  const dp=Array.from({length:n+1},()=>new Uint16Array(m+1));
  for(let i=n-1;i>=0;i--)for(let j=m-1;j>=0;j--){
    dp[i][j]=A[i]===B[j]?dp[i+1][j+1]+1:Math.max(dp[i+1][j],dp[i][j+1]);
  }
  const out=[]; let i=0,j=0;
  while(i<n&&j<m){
    if(A[i]===B[j]){out.push({type:"ctx",text:A[i],aLine:i+1,bLine:j+1});i++;j++;}
    else if(dp[i+1][j]>=dp[i][j+1]){out.push({type:"del",text:A[i],aLine:i+1});i++;}
    else {out.push({type:"add",text:B[j],bLine:j+1});j++;}
  }
  while(i<n){out.push({type:"del",text:A[i],aLine:i+1});i++;}
  while(j<m){out.push({type:"add",text:B[j],bLine:j+1});j++;}
  return out;
}

// One file in the diff block (expandable). before/after = target/source content.
function ScriptDiffFile({name, before, after}){
  const rows=useMemo(()=>computeLineDiff(before, after),[before,after]);
  const adds=rows.filter(r=>r.type==="add").length;
  const dels=rows.filter(r=>r.type==="del").length;
  const status=before==null?"neu":after==null?"entfernt":(adds||dels)?"geändert":"unverändert";
  const [open,setOpen]=useState(status!=="unveraendert");
  const statusColor=status==="neu"?"var(--iio-success)":status==="entfernt"?"var(--iio-critical)":status==="geaendert"?"var(--iio-warning)":"var(--iio-text-3)";
  return <div style={{border:"1px solid var(--iio-border)",borderRadius:"var(--iio-r-lg)",overflow:"hidden",marginBottom:10,background:"var(--iio-bg-1)"}}>
    <div onClick={()=>setOpen(o=>!o)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",cursor:"pointer",background:"var(--iio-bg-2)",borderBottom:open?"1px solid var(--iio-border)":"none"}}>
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="var(--iio-text-3)" strokeWidth="2" style={{transform:open?"rotate(90deg)":"none",transition:"transform .12s",flexShrink:0}}><path d="M6 4l4 4-4 4"/></svg>
      <span style={{fontFamily:"var(--iio-font-mono)",fontSize:12,color:"var(--iio-text-1)",fontWeight:600,flex:1,wordBreak:"break-all"}}>{name}</span>
      <span style={{fontFamily:"var(--iio-font-mono)",fontSize:10,fontWeight:700,letterSpacing:".06em",color:statusColor,textTransform:"uppercase"}}>{status}</span>
      {(adds>0||dels>0)&&<span style={{fontFamily:"var(--iio-font-mono)",fontSize:11,display:"flex",gap:6}}>
        <span style={{color:"var(--iio-success)"}}>+{adds}</span><span style={{color:"var(--iio-critical)"}}>-{dels}</span>
      </span>}
    </div>
    {open&&<div style={{overflow:"auto",maxHeight:"52vh"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontFamily:"var(--iio-font-mono)",fontSize:12,lineHeight:1.55}}>
        <tbody>
          {rows.map((r,idx)=>{
            const bg=r.type==="add"?"var(--iio-diff-add-bg)":r.type==="del"?"var(--iio-diff-del-bg)":"transparent";
            const sign=r.type==="add"?"+":r.type==="del"?"-":" ";
            const signCol=r.type==="add"?"var(--iio-success)":r.type==="del"?"var(--iio-critical)":"var(--iio-text-3)";
            return <tr key={idx} style={{background:bg}}>
              <td style={{width:44,textAlign:"right",padding:"0 6px",color:"var(--iio-text-4)",userSelect:"none",borderRight:"1px solid var(--iio-border-subtle)"}}>{r.aLine||""}</td>
              <td style={{width:44,textAlign:"right",padding:"0 6px",color:"var(--iio-text-4)",userSelect:"none",borderRight:"1px solid var(--iio-border-subtle)"}}>{r.bLine||""}</td>
              <td style={{width:16,textAlign:"center",color:signCol,userSelect:"none",fontWeight:700}}>{sign}</td>
              <td style={{padding:"0 8px",color:"var(--iio-text-1)",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{r.text||" "}</td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>}
  </div>;
}

// Script diff view: every script of the iFlow, changed ones first.
// The backend returns per file {before: target/previous content, after: source/new content}.
function ScriptDiffView({scripts}){
  const names=Object.keys(scripts||{}).sort((a,b)=>{
    const ca=scripts[a]?.before!==scripts[a]?.after, cb=scripts[b]?.before!==scripts[b]?.after;
    if(ca!==cb)return ca?-1:1; return a.localeCompare(b);
  });
  if(!names.length) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"var(--iio-text-3)"}}>Dieser iFlow enthält keine Skripte.</div>;
  const changed=names.filter(n=>scripts[n]?.before!==scripts[n]?.after).length;
  return <div style={{flex:1,overflow:"auto",minHeight:0}}>
    <div style={{fontSize:12,color:"var(--iio-text-2)",marginBottom:10}}>
      {changed>0?`${changed} von ${names.length} Skript(en) geändert - Vorher (Ziel) gegen Nachher (Quelle):`:`${names.length} Skript(e), keine Änderungen.`}
    </div>
    {names.map(n=><ScriptDiffFile key={n} name={n} before={scripts[n]?.before} after={scripts[n]?.after}/>)}
  </div>;
}

function parseContentModifierXml(val){
  if(!val||typeof val!=="string")return null;
  if(!val.includes("<row>")&&!val.includes("<row "))return null;
  try{
    const rows=[];
    const rowRe=/<row[^>]*>([\s\S]*?)<\/row>/g;
    let rm;
    while((rm=rowRe.exec(val))!==null){
      const cellRe=/<cell\s+id=["']([^"']*)["'][^>]*>([\s\S]*?)<\/cell>|<cell\s+id=["']([^"']*)["'][^>]*\/>/g;
      const row={};let cm;
      while((cm=cellRe.exec(rm[1]))!==null){
        const key=cm[1]||cm[3];const v=cm[2]!==undefined?cm[2]:"";
        if(key)row[key]=v;
      }
      if(Object.keys(row).length)rows.push(row);
    }
    if(!rows.length)return null;
    const ORDER=["Action","Type","Name","Value","Default","Datatype"];
    const cols=[...new Set([...ORDER,...rows.flatMap(r=>Object.keys(r))])].filter(k=>rows.some(r=>k in r));
    return{rows,cols};
  }catch{return null;}
}

// Element inspect popup

function ElementInspectPopup({element,side,parsedBefore,parsedAfter,diffIds,onClose,scripts}){
  const{theme}=useAppTheme();
  const isDark=theme==="dark";
  if(!element)return null;
  const elId=element.id||element;
  const info=side==="before"?parsedBefore[elId]:parsedAfter[elId];
  if(!info)return null;
  let status="Unverändert",statusColor="var(--text-3)",statusBg="var(--surface-2)";
  if(diffIds&&side!=="before"){
    if(diffIds.added.includes(elId)){status="Neu";statusColor="var(--iio-success)";statusBg="rgba(34,197,94,.12)";}
    else if(diffIds.removed.includes(elId)){status="Entfernt";statusColor="var(--iio-critical)";statusBg="rgba(239,68,68,.12)";}
    else if(diffIds.changed.includes(elId)){status="Geändert";statusColor="var(--iio-warning)";statusBg="rgba(251,146,60,.12)";}
  }
  const typeLabels={serviceTask:"Service Task",scriptTask:"Script Task",callActivity:"Call Activity",
    exclusiveGateway:"Router (Exclusive)",inclusiveGateway:"Inclusive Gateway",parallelGateway:"Parallel Gateway",
    startEvent:"Start Event",endEvent:"End Event",subProcess:"Sub Process",participant:"Participant",
    task:"Task",receiveTask:"Receive Task",sendTask:"Send Task",
    intermediateCatchEvent:"Intermediate Catch Event",intermediateThrowEvent:"Intermediate Throw Event"};
  const typeName=typeLabels[info.type]||info.type;
  let propDiffRows=null;
  if(diffIds&&diffIds.changed.includes(elId)&&parsedBefore[elId]&&parsedAfter[elId]){
    const bProps=parsedBefore[elId].properties||{};
    const aProps=parsedAfter[elId].properties||{};
    const allKeys=new Set([...Object.keys(bProps),...Object.keys(aProps)]);
    propDiffRows=[];
    for(const k of allKeys){
      const bv=bProps[k]!==undefined?bProps[k]:null;
      const av=aProps[k]!==undefined?aProps[k]:null;
      if(bv!==av)propDiffRows.push({key:k,before:bv,after:av});
    }
  }
  return <div style={{
    position:"absolute",top:8,right:8,left:typeof window!=="undefined"&&window.innerWidth<768?8:undefined,width:typeof window!=="undefined"&&window.innerWidth<768?"auto":320,maxHeight:420,overflow:"auto",
    background:isDark?"rgba(12,20,40,0.88)":"rgba(255,255,255,0.92)",
    backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",
    border:isDark?"1px solid rgba(45,212,191,0.25)":"1px solid rgba(20,184,166,0.3)",
    borderRadius:10,
    boxShadow:isDark?"0 8px 32px rgba(0,0,0,.5), 0 0 0 1px rgba(45,212,191,.1)":"0 8px 32px rgba(0,0,0,.12)",
    zIndex:20,fontSize:11
  }}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",
      borderBottom:"1px solid var(--border-subtle)",background:isDark?"rgba(45,212,191,0.06)":"rgba(20,184,166,0.06)"}}>
      <span style={{fontWeight:600,color:"var(--text-1)",fontSize:11}}>{info.name||elId}</span>
      <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-3)",fontSize:16,lineHeight:1,padding:0}}>&times;</button>
    </div>
    <div style={{padding:"8px 12px"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <span style={{fontSize:10,color:"var(--text-2)",fontFamily:MONO}}>{typeName}</span>
        {(info.activityType||info.subActivityType||info.cname)&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:4,
          background:isDark?"rgba(45,212,191,0.1)":"rgba(20,184,166,0.1)",
          color:isDark?"#2DD4BF":"#14B8A6",fontFamily:MONO}}>{info.activityType||(info.subActivityType||info.cname)}</span>}
        <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:statusBg,color:statusColor,fontWeight:600}}>{status}</span>
      </div>
      <div style={{fontSize:9,color:"var(--text-3)",fontFamily:MONO,marginBottom:8}}>ID: {elId}</div>
      {propDiffRows&&propDiffRows.length>0&&<div>
        <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>Geänderte Properties</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:MONO}}>
          <thead><tr style={{borderBottom:"1px solid var(--border-subtle)"}}>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--text-3)",fontWeight:600,fontSize:9}}>Property</th>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--text-3)",fontWeight:600,fontSize:9}}>Vorher</th>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--text-3)",fontWeight:600,fontSize:9}}>Nachher</th>
          </tr></thead>
          <tbody>{propDiffRows.map(r=>{
            const renderPropVal=(val,color)=>{
              const parsed=parseContentModifierXml(val);
              if(parsed){
                return <div style={{fontSize:9,overflow:"auto",maxHeight:120}}>
                  <table style={{borderCollapse:"collapse",width:"100%"}}>
                    <thead><tr>{parsed.cols.map(c=><th key={c} style={{padding:"2px 4px",color:"var(--text-3)",fontWeight:600,textAlign:"left",borderBottom:"1px solid var(--border-subtle)",whiteSpace:"nowrap"}}>{c}</th>)}</tr></thead>
                    <tbody>{parsed.rows.map((row,i)=><tr key={i}>{parsed.cols.map(c=><td key={c} style={{padding:"2px 4px",color:"var(--text-1)",borderBottom:"1px solid var(--border-subtle)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row[c]||"\u2014"}</td>)}</tr>)}</tbody>
                  </table>
                </div>;
              }
              return <span style={{color}}>{val===null?"\u2014":val||"(leer)"}</span>;
            };
            return <tr key={r.key} style={{background:"rgba(251,191,36,.08)"}}>
              <td style={{padding:"3px 4px",color:"var(--text-1)",fontWeight:500,maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.key}</td>
              <td style={{padding:"3px 4px",wordBreak:"break-all"}}>{renderPropVal(r.before,r.before===null?"var(--text-3)":"var(--iio-critical)")}</td>
              <td style={{padding:"3px 4px",wordBreak:"break-all"}}>{renderPropVal(r.after,r.after===null?"var(--text-3)":"var(--iio-success)")}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>}
      {propDiffRows&&propDiffRows.length===0&&<div style={{fontSize:10,color:"var(--text-3)"}}>Keine Property Unterschiede gefunden.</div>}
      {!propDiffRows&&info.properties&&Object.keys(info.properties).length>0&&<div>
        <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>Properties</div>
        <div style={{maxHeight:150,overflow:"auto"}}>
          {Object.entries(info.properties).map(([k,val])=>{
            const parsed=parseContentModifierXml(val);
            return <div key={k} style={{display:"flex",gap:4,fontSize:10,padding:"2px 0",borderBottom:"1px solid var(--border-subtle)",flexDirection:parsed?"column":"row"}}>
              <span style={{color:"var(--text-2)",fontFamily:MONO,minWidth:80}}>{k}</span>
              {parsed?<div style={{fontSize:9,overflow:"auto",maxHeight:120}}>
                <table style={{borderCollapse:"collapse",width:"100%"}}>
                  <thead><tr>{parsed.cols.map(c=><th key={c} style={{padding:"2px 4px",color:"var(--text-3)",fontWeight:600,textAlign:"left",borderBottom:"1px solid var(--border-subtle)",whiteSpace:"nowrap"}}>{c}</th>)}</tr></thead>
                  <tbody>{parsed.rows.map((row,i)=><tr key={i}>{parsed.cols.map(c=><td key={c} style={{padding:"2px 4px",color:"var(--text-1)",borderBottom:"1px solid var(--border-subtle)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row[c]||"\u2014"}</td>)}</tr>)}</tbody>
                </table>
              </div>:<span style={{color:"var(--text-1)",fontFamily:MONO,wordBreak:"break-all"}}>{val||"(leer)"}</span>}
            </div>;
          })}
        </div>
      </div>}
      {(info.activityType==="Script"||info.subActivityType==="GroovyScript"||info.subActivityType==="JavaScript"||info.cname==="GroovyScript"||info.properties?.script||info.properties?.scriptFilename||info.properties?.scriptName)&&(()=>{
        const scriptRef=info.properties?.script||info.properties?.scriptFilename||info.properties?.scriptName||"";
        const scriptFileName=scriptRef.split("/").pop();
        let scriptData=scripts?.[scriptFileName];
        if(!scriptData&&scripts){const k=Object.keys(scripts).find(k2=>k2.toLowerCase()===scriptFileName.toLowerCase());if(k)scriptData=scripts[k];}
        if(!scriptData&&scripts&&Object.keys(scripts).length>0){const k=Object.keys(scripts).find(k2=>k2.endsWith(".groovy")||k2.endsWith(".gsh")||k2.endsWith(".js"));if(k)scriptData=scripts[k];}
        const content=side==="before"?(scriptData?.before||scriptData?.after):scriptData?.after;
        const resolvedName=scriptFileName||Object.keys(scripts||{})[0]||"Script";
        if(!content)return <div style={{marginTop:8}}>
          <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>Script</div>
          <div style={{fontSize:9,color:"var(--text-3)",fontStyle:"italic",padding:"4px 0"}}>Script Inhalt nicht verfügbar.</div>
        </div>;
        return <div style={{marginTop:8}}>
          <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>Script - {resolvedName}</div>
          <pre style={{margin:0,padding:"6px 8px",background:isDark?"rgba(0,0,0,0.3)":"rgba(0,0,0,0.05)",borderRadius:4,fontSize:9,fontFamily:MONO,color:isDark?"#a5f3fc":"#0f766e",maxHeight:180,overflow:"auto",whiteSpace:"pre-wrap",wordBreak:"break-all"}}>
            {content.length>800?content.substring(0,800)+"\n[...]":content}
          </pre>
        </div>;
      })()}
    </div>
  </div>;
}

// Diff legend (labels defined below in `items`).
// Same visual language as the central IflowDiagram component: a colored
// badge (+/-/delta) plus a plain-text prefix, so color never carries the
// meaning alone.

function DiffLegend(){
  const items=[
    ["var(--iio-success)","+","NEU"],
    ["var(--iio-critical)","−","WEG"],
    ["var(--iio-warning)","Δ","GEÄND."],
  ];
  return <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",
    fontSize:11,padding:"6px 12px",borderRadius:"var(--iio-r-md)",
    background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)"}}>
    {items.map(([col,badge,lbl])=>
      <span key={lbl} style={{display:"flex",alignItems:"center",gap:6}}>
        <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
          width:16,height:16,borderRadius:"50%",background:col,color:"var(--iio-bg-2)",
          fontSize:11,fontWeight:700,lineHeight:1,fontFamily:"Source Sans 3,sans-serif"}}>{badge}</span>
        <span style={{fontWeight:700,letterSpacing:".04em",color:col}}>{lbl}</span>
      </span>
    )}
  </div>;
}

// Diagram pane (central IflowDiagram renderer).
// Rendering comes entirely from IflowDiagram; this only adds the header row,
// the click inspector, and passing the diff marks/onlyChanges through to
// the component.

function ComparePane({xml,label,side,marks,onlyChanges,parsedBefore,parsedAfter,diffIds,scripts}){
  const[inspected,setInspected]=useState(null);
  return <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>
    <div style={{fontSize:11,fontWeight:600,color:"var(--iio-text-2)",padding:"6px 10px",
      background:"var(--iio-bg-2)",border:"1px solid var(--iio-border)",borderBottom:"none",
      borderRadius:"var(--iio-r-md) var(--iio-r-md) 0 0",display:"flex",alignItems:"center",gap:8}}>
      <span style={{width:8,height:8,borderRadius:2,flexShrink:0,background:side==="before"?"var(--iio-critical)":"var(--iio-success)"}}/>
      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>
    </div>
    <div style={{flex:1,minHeight:380,position:"relative",overflow:"hidden",
      border:"1px solid var(--iio-border)",borderRadius:"0 0 var(--iio-r-md) var(--iio-r-md)"}}
      onClick={()=>setInspected(null)}>
      <IflowDiagram bpmnXml={xml} height="100%" marks={marks} onlyChanges={onlyChanges}
        onElementClick={(el)=>setInspected(el)} selectedId={inspected?.id}/>
      {inspected&&<ElementInspectPopup element={inspected} side={side}
        parsedBefore={parsedBefore||{}} parsedAfter={parsedAfter||{}} diffIds={diffIds}
        onClose={()=>setInspected(null)} scripts={scripts}/>}
    </div>
  </div>;
}

// Main compare dialog

export default function BpmnCompareDialog({open,onClose,sourceInstanceId,targetInstanceId,artifactId,targetArtifactId,artifactName,sourcePackageId,targetPackageId,sourceVersion,targetVersion}){
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState(null);
  const[data,setData]=useState(null);

  useEffect(()=>{
    if(!open||!artifactId)return;
    setLoading(true);setError(null);setData(null);
    api("/api/transport/compare-bpmn",{method:"POST",
      body:JSON.stringify({sourceInstanceId,targetInstanceId,artifactId,targetArtifactId:targetArtifactId||artifactId,sourcePackageId,targetPackageId})
    }).then(d=>{setData(d);setLoading(false);}).catch(e=>{setError(e.message||"Fehler beim Laden");setLoading(false);});
  },[open,artifactId,sourceInstanceId,targetInstanceId,targetArtifactId,sourcePackageId,targetPackageId]);

  const tgtBpmn=data?.target?.bpmn;
  const srcBpmn=data?.source?.bpmn;
  const parsedBefore=useMemo(()=>tgtBpmn?parseBpmnElements(tgtBpmn):{}, [tgtBpmn]);
  const parsedAfter=useMemo(()=>srcBpmn?parseBpmnElements(srcBpmn):{}, [srcBpmn]);
  const diffIds=useMemo(()=>tgtBpmn&&srcBpmn?diffBpmnElements(parsedBefore,parsedAfter):null, [parsedBefore,parsedAfter,tgtBpmn,srcBpmn]);
  const hasChanges=diffIds&&(diffIds.added.length+diffIds.removed.length+diffIds.changed.length>0);
  const totalChanges=diffIds?(diffIds.added.length+diffIds.removed.length+diffIds.changed.length):0;
  // Marks for the central IflowDiagram component. The rule is "what does the transport
  // change":
  //   before/target pane (old version) -> removed ("del") and changed ("chg") elements
  //   after/source pane (new version) -> added ("add") and changed ("chg") elements
  const marksBefore=useMemo(()=>{
    if(!diffIds)return undefined;
    const m={};
    for(const id of diffIds.removed)m[id]="del";
    for(const id of diffIds.changed)m[id]="chg";
    return m;
  },[diffIds]);
  const marksAfter=useMemo(()=>{
    if(!diffIds)return undefined;
    const m={};
    for(const id of diffIds.added)m[id]="add";
    for(const id of diffIds.changed)m[id]="chg";
    return m;
  },[diffIds]);
  const[onlyChanges,setOnlyChanges]=useState(false);
  const scripts=data?.scripts||{};
  const scriptNames=Object.keys(scripts);
  const changedScripts=scriptNames.filter(n=>scripts[n]?.before!==scripts[n]?.after).length;
  const [activeTab,setActiveTab]=useState("diagram");
  useEffect(()=>{ if(open){ setActiveTab("diagram"); setOnlyChanges(false); } },[open,artifactId]);
  const sourceError=data?.source?.error;
  const targetError=data?.target?.error;

  const beforeLabel=`Vorher - ${data?.target?.instanceName||"Zielsystem"}${targetVersion?" v"+targetVersion:""}`;
  const afterLabel=`Nachher - ${data?.source?.instanceName||"Quellsystem"}${sourceVersion?" v"+sourceVersion:""}`;

  const tabDef=[
    {k:"diagram",l:"Diagramm",badge:totalChanges>0?totalChanges:null},
    {k:"scripts",l:"Scripte",badge:changedScripts>0?changedScripts:null},
  ];

  return <MuiDialog open={open} onClose={onClose} maxWidth={false} fullWidth
    PaperProps={{className:"iio",sx:{
      maxWidth:"95vw",width:"95vw",height:"88vh",
      background:"var(--iio-bg-0)",
      border:"1px solid var(--iio-border)",
      borderRadius:"var(--iio-r-xl)",overflow:"hidden"
    }}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 20px",
      borderBottom:"1px solid var(--iio-border)",
      background:"var(--iio-bg-2)"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--iio-signal)" strokeWidth="2" strokeLinecap="round">
          <path d="M12 3v18M3 12h18M8 8l-4 4 4 4M16 8l4 4-4 4"/>
        </svg>
        <div style={{minWidth:0}}>
          <div style={{fontSize:14,fontWeight:600,color:"var(--iio-text-1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Änderungen - {artifactName||artifactId}</div>
          <div style={{fontSize:11,color:"var(--iio-text-3)",display:"flex",alignItems:"center",gap:8}}>
            {sourceVersion&&targetVersion&&<span style={{display:"inline-flex",alignItems:"center",gap:6}}>
              <span>v{targetVersion}</span>
              <span className="iio-mon2-chevflow iio-mon2-chevflow--static iio-mon2-chevflow--sm" aria-hidden="true"><i/><i/></span>
              <span style={{color:"var(--iio-success)",fontWeight:600}}>v{sourceVersion}</span>
            </span>}
          </div>
        </div>
      </div>
      <MuiIconButton onClick={onClose} size="small" sx={{color:"var(--iio-text-3)"}}><CloseIcon fontSize="small"/></MuiIconButton>
    </div>

    {/* Tab bar: Diagramm | Scripte (shared iio-mon2-subtab pattern) */}
    <div className="iio-mon2-subtabs" style={{margin:0,padding:"0 20px",background:"var(--iio-bg-1)"}}>
      {tabDef.map(t=>(
        <button key={t.k} className={`iio-mon2-subtab${activeTab===t.k?" is-active":""}`} onClick={()=>setActiveTab(t.k)}>
          {t.l}
          {t.badge!=null&&<span className="cnt" style={{color:activeTab===t.k?"var(--iio-signal)":"var(--iio-text-3)",fontFamily:"var(--iio-font-mono)"}}>{t.badge}</span>}
        </button>
      ))}
    </div>

    <div style={{flex:1,overflow:"auto",padding:16,display:"flex",flexDirection:"column",gap:8}}>
      {loading&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12}}>
        <div style={{width:32,height:32,border:"3px solid var(--iio-border)",borderTopColor:"var(--iio-signal)",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
        <div style={{fontSize:12,color:"var(--iio-text-3)"}}>iFlow wird geladen...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>}

      {error&&<div style={{padding:"12px 14px",color:"var(--iio-critical)",fontSize:12,background:"var(--iio-bg-1)",borderRadius:"var(--iio-r-md)",borderLeft:"3px solid var(--iio-critical)",border:"1px solid var(--iio-border)"}}>
        Fehler: {error}
      </div>}

      {!loading&&!error&&data&&<>
        {(sourceError||targetError)&&<div style={{padding:"10px 14px",borderRadius:"var(--iio-r-md)",background:"var(--iio-bg-1)",borderLeft:"3px solid var(--iio-warning)",border:"1px solid var(--iio-border)",fontSize:11,color:"var(--iio-warning)",display:"flex",flexDirection:"column",gap:4}}>
          {sourceError&&<div>Quellsystem: {sourceError}</div>}
          {targetError&&<div>Zielsystem: {targetError}</div>}
        </div>}

        {activeTab==="scripts"
          ?<ScriptDiffView scripts={scripts}/>
          :<>
            {hasChanges&&<div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <DiffLegend/>
              <label style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto",fontSize:12,color:"var(--iio-text-2)",cursor:"pointer",userSelect:"none"}}>
                <input type="checkbox" checked={onlyChanges} onChange={e=>setOnlyChanges(e.target.checked)} style={{accentColor:"var(--iio-signal)",cursor:"pointer",width:14,height:14}}/>
                Nur Änderungen
              </label>
            </div>}
            {!hasChanges&&tgtBpmn&&srcBpmn&&<div style={{padding:"8px 12px",borderRadius:"var(--iio-r-md)",background:"var(--iio-bg-1)",borderLeft:"3px solid var(--iio-success)",border:"1px solid var(--iio-border)",fontSize:11,color:"var(--iio-success)"}}>
              Keine strukturellen Änderungen im iFlow.{changedScripts>0?` Aber ${changedScripts} Skript(e) geändert - siehe Tab "Scripte".`:" Unterschiede möglicherweise in Parametern."}
            </div>}
            {(srcBpmn||tgtBpmn)?<div style={{display:"flex",gap:8,flex:1,minHeight:0}}>
              {tgtBpmn
                ?<ComparePane xml={tgtBpmn} label={beforeLabel} side="before" marks={marksBefore} onlyChanges={onlyChanges} parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds} scripts={scripts}/>
                :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:6,fontSize:11,color:"var(--iio-text-3)"}}>Kein Vorher-BPMN verfügbar (neues Artefakt?)</div>}
              {srcBpmn
                ?<ComparePane xml={srcBpmn} label={afterLabel} side="after" marks={marksAfter} onlyChanges={onlyChanges} parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds} scripts={scripts}/>
                :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:6,fontSize:11,color:"var(--iio-text-3)"}}>Kein Nachher-BPMN verfügbar</div>}
            </div>
            :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"var(--iio-text-3)"}}>
              Kein BPMN-XML verfügbar. Stelle sicher, dass beide Artefakte existieren.
            </div>}
          </>}
      </>}
    </div>
  </MuiDialog>;
}
